import { Injectable, NgZone, makeStateKey } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { get } from 'lodash-es';

import { OutletApiService } from '@core/http/outlet-api.service';
import { StorageService } from '@core/services/util/storage.service';
import { PlatformService } from '@core/services/util/platform.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { MenuService } from '@core/services/menu/menu.service';
import { CartService } from '@core/services/cart/cart.service';
import { UtilityService } from '@core/services/util/utility.service';
import { DeviceService } from '@core/services/util/device.service';
import { SentryBreadcrumbsService } from '@app/core/services/sentry-breadcrumbs.service';

import { environment } from '@environment';

import { PictureShape } from '@shared/enums/picture-shape.enum';
import { OutletType } from '@shared/models/outlet-type';

import { ImageUrlCreatorService } from '@core/services/imageUrlCreator/image-url-creator.service';
import { MembershipService } from '@core/services/membership/membership.service';
import { GtmService } from '../analytics/gtm.service';

import { PricableType } from '@app/shared/enums/menu.enum';
import { AddressService } from '../address/address.service';
import { PreorderService } from '@core/services/preorder/preorder.service';
import { LocalStorageService } from '../util/local-storage.service';
import { OutletChangeErrorComponent } from '@app/core/components/header/components/outlet-change-error/outlet-change-error.component';
import { MatDialog } from '@angular/material/dialog';
import { PslaService } from '../psla/psla.service';
import { Router } from '@angular/router';
import { PassService } from '../pass/pass.service';
import { ToastrService } from '../notification/toastr.service';
import { CatalogApiResponse, OutletStatus } from '@app/shared/models/api-response/catalog-api-response';

const OUTLET_DATA = makeStateKey('outlet_data');

@Injectable({
    providedIn: 'root'
})
export class OutletService {
    state = new BehaviorSubject<any>({});
    brandId = new BehaviorSubject(null);

    constructor(
        private outletApiService: OutletApiService,
        private menuService: MenuService,
        private platformService: PlatformService,
        private sessionStorage: SessionStorageService,
        private storageService: StorageService,
        private utilityService: UtilityService,
        private cartService: CartService,
        private deviceService: DeviceService,
        private imageUrlCreator: ImageUrlCreatorService,
        private membershipService: MembershipService,
        private addressService: AddressService,
        private breadCrumb: SentryBreadcrumbsService,
        private gtmService: GtmService,
        private preorderService: PreorderService,
        private localStorage: LocalStorageService,
        private dialog: MatDialog,
        private zone: NgZone,
        private pslaService: PslaService,
        private passService: PassService,
        private router: Router,
        private toastrService: ToastrService
    ) {
    }

    afterOutletChangeTasks() {
        if (this.platformService.isBrowser()) {
            this.cartService.resetCart();
            this.cartService.getCart();
            this.preorderService.servicableBrandMetaData.next(null);
            this.passService.resetCustomerPasses();
            this.router.navigateByUrl('/');
        }
    }

    getOutletDataKey(outlet_id: number) {
        return `${OUTLET_DATA}_${outlet_id}`;
    }

    isStaleOutletData(outletDataKey: string, sessionData) {
        // remove stale outlet data
        const isDataRemoved = this.removeStaleOutletData(outletDataKey, sessionData);

        if (isDataRemoved) {
            this.sessionStorage.remove(`${outletDataKey}`);
            this.sessionStorage.remove(`${outletDataKey}_last_fetched`);
            return true;
        }
        return false;
    }

    removeStaleOutletData(outletDataKey: string, sessionData) {
        const lastFetched = this.sessionStorage.get(`${outletDataKey}_last_fetched`);
        const currentTime = new Date() as any;
        const currentHour = currentTime.getHours();

        const isRushHour = (currentHour >= 12 && currentHour < 14) || (currentHour >= 19 && currentHour < 22);
        const isDefaultOutlet = sessionData && sessionData.outlet?.dflt;

        const timeDifference = currentTime - lastFetched;

        if (isDefaultOutlet) {
            if (timeDifference >= 10 * 60 * 1000) {
                return true;
            }
        } else if (isRushHour && timeDifference >= 0.5 * 60 * 1000) {
            return true;
        } else if (timeDifference >= 2 * 60 * 1000) {
            return true;
        }
        return false;
    }

    async getOutletData(brand_id: number) {
        if (this.platformService.isServer()) {
            return;
        }

        const outletDataKey = this.getOutletDataKey(brand_id);
        this.brandId.next(brand_id);

        let sessionData = this.sessionStorage.get(outletDataKey);

        if (this.isStaleOutletData(outletDataKey, sessionData)) {
            sessionData = null;
        }

        if (sessionData) {
            const outletData = this.hydrate(sessionData);
            return { ...sessionData, outlet: outletData };
        }

        try {
            const params = {};
            this.getPreferredOutletParams(params);
            const data = await this.outletApiService.near(params, brand_id).toPromise();
            if (data && data.meta && Math.floor(data.meta.code / 10) === 20) {
                const outletData = this.hydrate(data);
                return { ...data, outlet: outletData };
            } else {
                return {};
            }
        } catch (e) {
            console.log(e, 'Error in getOutletData');
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            return {};
        }
    }

    async getOutletDataWithoutStateUpdate(brand_id: number) {
        try {
            const outletDataKey = this.getOutletDataKey(brand_id);
            let sessionData = this.sessionStorage.get(outletDataKey);

            if (this.isStaleOutletData(outletDataKey, sessionData)) {
                sessionData = null;
            }

            if (sessionData) {
                return sessionData;
            }

            const params = {};
            this.getPreferredOutletParams(params);
            const data = await this.outletApiService.near(params, brand_id).toPromise();

            if (!data || !data.outlet) {
                return {};
            }

            this.saveOutletData(outletDataKey, data);
            this.saveMetaDataToStorage(data);
            return data;
        } catch (e) {
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            return {};
        }
    }

    sort(data: any, key: string) {
        return data.sort(function (x, y) {
            if (x[key] > y[key]) {
                return 1;
            } else if (x[key] < y[key]) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    createItemImageUrl(item_type: string, item_id: number) {
        const resolution = this.deviceService.getResolution();
        let imageShape = '';
        switch (item_type) {
            case 'banner':
                imageShape = 'horizontal-rectangle';
                break;

            case 'product':
                imageShape = 'square';
                break;

            default:
                imageShape = 'default-picture-shape';
        }

        return environment.assetsUrl + `/${imageShape}/${resolution}/` + item_type + '/' + item_id;
    }

    createCategoryImageUrl(shape: string, id: number) {
        const resolution = this.deviceService.getResolution();
        let picture_shape = PictureShape.SQUARE;

        let version = '';
        if (this.deviceService.isMobile()) {
            if (shape === 'rectangle') {
                picture_shape = PictureShape.HORIZONTAL_RECTANGLE;
            }
            version = '-v2';
        }

        return environment.assetsUrl + '/' + picture_shape + version + '/' + resolution + '/category' + '/' + id;
    }

    createCategoryIcon(id) {
        const shape = environment.brand.id === 19 ? 'category-icon-v2' : 'category-icon';
        return environment.assetsUrl + `/${shape}/web/category/${id}`;
    }

    private _addOutletMetaData(outlet: any) {
        const outletMetaData: any = Object.create(null);

        // adding announcement
        if (outlet.announcement) {
            outletMetaData.announcement = {
                ...outlet.announcement,
                image_url: this.createItemImageUrl('announcement', outlet.announcement.id)
            };
        }

        // adding membership details
        if ('memberships' in outlet) {
            outletMetaData.memberships = outlet.memberships.map((membership: any) => {
                if (outlet.membership_actions[membership.id]) {
                    membership['override_action'] = outlet.membership_actions[membership.id];
                }
                return { ...membership, image: this.imageUrlCreator.getMembershipImage(membership) };
            });
        }

        // adding banners
        if (outlet.brand_id === environment.brand.id) {
            outletMetaData.banners = {};
            Object.keys(outlet.banners).forEach(data => {
                outletMetaData.banners[data] = outlet.banners[data].map((banner: any) => {
                    return { ...banner, image: this.createItemImageUrl('banner', banner.id) };
                });
            });
        } else {
            outletMetaData.banners = outlet.banners.map((banner: any) => {
                if (outlet.banner_actions[banner.id]) {
                    banner['override_action'] = outlet.banner_actions[banner.id];
                }
                return { ...banner, image: this.createItemImageUrl('banner', banner.id) };
            });
        }

        return outletMetaData;
    }

    addOutletMenuData(outlet: any) {
        const outletMenuData: any = Object.create(null);
        const keysToHash = ['product_categories', 'product_sizes', 'product_customizations', 'sampling_ids'];
        const menuHash: any = Object.create(null);

        keysToHash.forEach((key: string) => {
            if (key in outlet) {
                menuHash[key] = menuHash[key] || Object.create(null);
                for (let i = 0; i < outlet[key].length; i++) {
                    menuHash[key][outlet[key][i].id] = outlet[key][i];
                }
            }
        });

        const category_ids = new Set(outlet.products.map((product: any) => product.category_id));
        outletMenuData.product_categories = outlet.product_categories
            .filter(x => category_ids.has(x.id))
            .map((category: any) => {
                if (outlet.category_actions[category.id]) {
                    category['override_action'] = outlet.category_actions[category.id];
                }
                return {
                    ...category,
                    image: this.createCategoryImageUrl(category.shape, category.id),
                    icon: this.createCategoryIcon(category.id)
                };
            });

        const productSoldOut = {};
        outletMenuData.product_sizes = [];
        outletMenuData.product_customizations = [];

        outlet.menu_items.forEach(menu_item => {
            if (menu_item.ptype === PricableType.ProductSize) {
                const product_size = menuHash.product_sizes[menu_item.pid];

                if (product_size) {
                    if (productSoldOut[product_size.product_id] !== undefined) {
                        productSoldOut[product_size.product_id] = productSoldOut[product_size.product_id] && menu_item.sold_out;
                    } else {
                        productSoldOut[product_size.product_id] = menu_item.sold_out;
                    }

                    outletMenuData.product_sizes.push({
                        ...product_size,
                        label: product_size.label || '',
                        description: product_size.description || '',
                        prices: {
                            ...menu_item.prices,
                            subscription_prices: []
                        },
                        price: menu_item.prices.discounted_price,
                        marked_price: menu_item.prices.price,
                        sold_out: menu_item.sold_out,
                        image: this.createItemImageUrl('productsize', product_size.id)
                    });
                }
            } else if (menu_item.ptype === PricableType.ProductCustomization) {
                const product_customization = menuHash.product_customizations[menu_item.pid];
                if (product_customization) {
                    outletMenuData.product_customizations.push({
                        ...product_customization,
                        prices: menu_item.prices,
                        price: menu_item.prices.discounted_price,
                        marked_price: menu_item.prices.price,
                        sold_out: menu_item.sold_out,
                        image: this.createItemImageUrl('productcustomization', product_customization.id)
                    });
                }
            }
        });

        // get products
        outletMenuData.products = outlet.products
            .map((product: any) => {
                if (productSoldOut[product.id] !== undefined) {
                    const isSoldOut = productSoldOut[product.id];
                    const product_layout = this.membershipService.getProductLayout(outlet);
                    return {
                        ...product,
                        image: this.imageUrlCreator.getProductImage(product.id, product_layout),
                        sold_out: isSoldOut
                    };
                }
            })
            .filter(Boolean);

        outletMenuData.sampling_ids = outlet.sampling_details?.items.map(data => data.product_size_id) || [];

        const keysToExcludeFromSorting = ['product_categories'];
        Object.keys(outletMenuData).forEach(key => {
            if (
                outletMenuData[key] instanceof Array &&
                outletMenuData[key].length > 0 &&
                outletMenuData[key][0] &&
                Boolean(outletMenuData[key][0].sequence)
            ) {
                if (keysToExcludeFromSorting.indexOf(key) === -1) {
                    outletMenuData[key] = this.sort(outletMenuData[key], 'sequence');
                }
            }
        });

        return outletMenuData;
    }

    convertOutletData(data: CatalogApiResponse) {
        const { outlet } = data;
        const outletMetaData: any = this._addOutletMetaData(outlet);
        const menuData: any = this.addOutletMenuData(outlet);
        const outletStatusData = this.addOutletStatusData(data);
        return { ...outlet, ...outletMetaData, ...menuData, ...outletStatusData };
    }

    addOutletStatusData(data: CatalogApiResponse): {
        outlet_state: string;
        outlet_status_strip: OutletStatus | null;
    } {
        const { meta, outlet } = data;
        const { heading, message, allow_refresh, icon } = meta;
        const outlet_state = this.utilityService.getOutletState(outlet);
        let outlet_status_strip = null;

        if (heading && message) {
            outlet_status_strip = {
                icon,
                heading,
                message,
                allow_refresh
            };
        }
        return {
            outlet_state,
            outlet_status_strip
        };
    }

    private _constructLogParams(source, brandId, metaCode) {
        const lat = this.storageService.get('latitude') || 0;
        const lon = this.storageService.get('longitude') || 0;
        const currentTime = new Date();
        this.gtmService.logEvent('js_errors', {
            event_label: source,
            event_value: `${brandId}-${lat}-${lon}-${currentTime}-${metaCode}`
        });
    }

    hydrate(data: any, source?: string, brandId?: number) {
        if (!data.outlet) {
            this._constructLogParams(source, brandId, data.meta?.code);
            return;
        }

        const { brand_id } = data.outlet;

        this.breadCrumb.add('Outlet Data', 'Hydrate', brand_id);

        const outletDataKeys = this.getOutletDataKey(brand_id);
        this.brandId.next(brand_id);

        this.saveOutletData(outletDataKeys, data);

        // setting basic info, for future api calls

        this.saveMetaDataToStorage(data);

        const outletData = this.convertOutletData(data);
        this.menuService.parseOutletData(outletData);
        this.state.next({
            outlet: outletData,
            meta: data.meta,
            nearby: data.nearby,
            outside_link: data.outside_link
        }); // pushing data to behaviour subject
        return outletData;
    }

    saveMetaDataToStorage(data) {
        const { brand_id, id, menu_id, service_area_id, agent_id, outlet_service_types, address_id, service_type } = data.outlet;

        if (brand_id === environment.brand.id) {
            const expiryTime = this.utilityService.getDefaultCookieExpiryTime();
            this.storageService.set('outlet_id', id || 1, expiryTime);
            this.storageService.set('menu_id', menu_id, expiryTime);
            this.storageService.set('service_area_id', service_area_id, expiryTime);
            if (service_type) {
                this.storageService.set('service_type', service_type, expiryTime);
            }

            if (agent_id) {
                this.storageService.set('agent_id', agent_id);
            }

            // setting variable to show to user in case of not given anything
            const outletLocality = this.storageService.get('outlet_locality');
            if (!outletLocality) {
                this.storageService.set('outlet_locality', 'Showing Default Location', expiryTime);
                // delete cart for default location
                this.cartService.resetCart();
            }

            // setting address locality
            this.storageService.set('outlet_address', address_id);
            this.addressService.setLocality(outlet_service_types);
        }
    }

    saveOutletData(outletDataKey, data) {
        this.sessionStorage.set(outletDataKey, data); // adding data to session storage
        const lastFetched = this.sessionStorage.get(`${outletDataKey}_last_fetched`);
        if (!lastFetched) {
            this.sessionStorage.set(`${outletDataKey}_last_fetched`, Date.now());
        }
    }

    setLocationInCookie(options: any, outletServiceTypes = []) {
        const expiryTime = this.utilityService.getDefaultCookieExpiryTime();

        const latitude = options.lat || options.latitude;
        const longitude = options.lon || options.longitude;
        const locality = options.formatted_locality || options.locality;
        const addressId = options.address_id || options.id;

        this.storageService.set('latitude', latitude, expiryTime);
        this.storageService.set('longitude', longitude, expiryTime);
        this.storageService.set('outlet_locality', locality, expiryTime);

        if (addressId) {
            this.storageService.set('address_id', String(addressId), expiryTime);
            this.storageService.set('service_type', 'DEL101', expiryTime);
        } else {
            // incase user added new address, which is not saved this will be not present
            this.storageService.delete('address_id');
        }

        if (options.address_type) {
            this.storageService.set('address_type', options.address_type);
        } else {
            // incase user added new address, which is not saved this will be not present
            this.storageService.delete('address_type');
        }

        this.addressService.setLocality(outletServiceTypes);
    }

    // clean all brand data on outlet changg
    deleteAllOutletData() {
        this.storageService.delete('outlet_id');
        this.storageService.delete('agent_id');
        this.storageService.delete('service_type');
        this.sessionStorage.remove('wa_aid');
        this.sessionStorage.remove('wa_os');
        this.sessionStorage.remove('psla_data');

        this.storageService.delete('outlet_selection_shown');
        Object.keys(sessionStorage).forEach((key: string) => {
            if (key.includes(OUTLET_DATA) || key.includes('announcement_img_url')) {
                this.sessionStorage.remove(key);
            }
        });
    }

    getPreferredOutletParams(params) {
        const outlet_id = this.storageService.get('outlet_id');
        if (outlet_id) {
            params['outlet_id'] = outlet_id;
        }

        const agent_id = this.storageService.get('agent_id');
        if (agent_id) {
            params['agent_id'] = agent_id;
        }

        const service_type = this.storageService.get('service_type');
        if (service_type) {
            params['service_type'] = service_type;
        }
    }

    async outletRefresh(outletParams: any) {
        try {
            const { lat, lon, locality, address_type, address_id, service_type } = outletParams;
            const brand_id = environment.brand.id;

            const data = await lastValueFrom(this.outletApiService.near({ lat, lon, service_type }, brand_id));
            if (data && data.meta && Math.floor(data.meta.code / 10) === 20) {
                this.deleteAllOutletData();
                this.afterOutletChangeTasks();
                this.setLocationInCookie({ lat, lon, locality, address_type, address_id });
                this.hydrate(data);
                this.addressService.fetch();
                return data;
            }
            return {};
        } catch (e) {
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            return {};
        }
    }

    async getOutletById(outlet_params) {
        try {
            const data = await lastValueFrom(this.outletApiService.near(outlet_params));
            if (data && data.meta && Math.floor(data.meta.code / 10) === 20) {
                this.deleteAllOutletData();
                this.afterOutletChangeTasks();
                this.hydrate(data);
                return data;
            }

            return {};
        } catch (e) {
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            return {};
        }
    }

    outletInside(outletParams: OutletType) {
        const { lat, lon } = outletParams;
        return this.outletApiService.inside({ lat, lon, outlet_id: get(this.state.value, 'outlet.id', 1) });
    }

    changeOutlet(options: any) {
        this.zone.run(() => {
            this.outletRefresh(options);
        });
    }

    async checkForOutletChange(options: any) {
        const isLocationInside = await this.isLocationInside(options);
        if (isLocationInside) {
            const currentServiceType = this.storageService.get('service_type');

            const active_brand = environment.brand.id;
            const primaryBrandData = await this.getOutletDataWithoutStateUpdate(active_brand);

            const { id, distinct_menus, outlet_service_types, banners } = primaryBrandData.outlet;
            this.setLocationInCookie(options, outlet_service_types);

            if (options.service_type === 'DEL101' && currentServiceType !== 'DEL101' && distinct_menus) {
                this.gtmService.logWebAnalyticsEvent('service_type_change', 'Service type changed: ' + id);
                this.changeOutlet(options);
            } else {
                this.sessionStorage.remove('psla_data');
                if (this.deviceService.isMobile()) {
                    const promotionalBannerPresent = banners['main_promotional_card'] && banners['main_promotional_card'].length;
                    this.pslaService.getPSLAdata(promotionalBannerPresent);
                }
            }
        } else {
            // check if there is item in cart
            const cart = this.localStorage.get('cart');
            if (cart && Object.keys(cart.items).length) {
                this.zone.run(() => {
                    this.dialog
                        .open(OutletChangeErrorComponent, { maxWidth: 450 })
                        .afterClosed()
                        .subscribe((result: string) => {
                            if (result === 'proceed') {
                                this.changeOutlet(options);
                            }
                        });
                });
                return;
            }

            this.changeOutlet(options);
        }
    }

    async isLocationInside(options: OutletType) {
        const { lat, lon, locality } = options;
        try {
            const response: any = await this.outletInside({ lat, lon, locality }).toPromise();
            return response.meta.code === 200 ? true : false;
        } catch (e) {}
    }

    async checkOutletStatus(data) {
        try {
            const { brand_id, open, active } = data;
            const params = { open, active };

            this.getPreferredOutletParams(params);

            const response = await lastValueFrom(this.outletApiService.near(params, brand_id));

            if (response && response.meta && Math.floor(response.meta.code / 10) === 20) {
                const activeBrand = this.storageService.get('brand_id');

                // Incase user is on menu page, hydrate with fresh catalog data, else simply store the updated catalog data.
                if (brand_id === activeBrand) {
                    const outletData = this.hydrate(response);
                    return { ...response, outlet: outletData };
                } else {
                    const outletDataKey = this.getOutletDataKey(brand_id);
                    this.saveOutletData(outletDataKey, response);
                    this.saveMetaDataToStorage(response);
                }

                return response;
            }

            return {};
        } catch (e) {
            return {};
        }
    }

    getDistanceFromLatLonInMeter(lat1: any, lon1: any, lat2: number, lon2: number): number {
        return this.utilityService.getDistanceFromLatLonInMeter(lat1, lon1, lat2, lon2);
    }

    isOutletSelected() {
        const cookieOutletId = Number(this.storageService.get('outlet_id'));
        const outletLocality = this.storageService.get('outlet_locality');
        if (cookieOutletId === 1 && (!outletLocality || outletLocality === 'Showing Default Location')) {
            return false;
        }
        return true;
    }
}
